import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { MotionConfig } from "framer-motion";
import Lenis from "lenis";
import { Toaster } from "sonner";
import "@/App.css";
import { ContentProvider, IntroCtx } from "./lib/ContentContext";
import { lenisRef, prefersReduced } from "./lib/lenis";
import { Nav } from "./components/Nav";
import { Footer } from "./components/Footer";
import { Loader } from "./components/Loader";
import Home from "./pages/Home";

const About = lazy(() => import("./pages/About"));
const Platforms = lazy(() => import("./pages/Platforms"));
const Indril = lazy(() => import("./pages/Indril"));
const Market = lazy(() => import("./pages/Market"));
const AIOS = lazy(() => import("./pages/AIOS"));
const Founder = lazy(() => import("./pages/Founder"));
const Vision = lazy(() => import("./pages/Vision"));
const Contact = lazy(() => import("./pages/Contact"));
const Legal = lazy(() => import("./pages/Legal"));
const News = lazy(() => import("./pages/NewsMedia"));
const Article = lazy(() => import("./pages/Article"));
const Admin = lazy(() => import("./pages/Admin"));

const NotFound = () => (
  <section className="min-h-[80vh] flex flex-col items-center justify-center text-center px-6" data-testid="not-found">
    <p className="eyebrow mb-6">404</p>
    <h1 className="font-display text-5xl font-bold">Page not found</h1>
    <Link to="/" className="btn-gold mt-10 rounded-full px-8 py-4 text-sm font-semibold" data-testid="not-found-home-link">Back to home</Link>
  </section>
);

function useLenis() {
  useEffect(() => {
    if (prefersReduced()) return undefined;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    lenisRef.current = lenis;
    let id;
    const raf = (t) => { lenis.raf(t); id = requestAnimationFrame(raf); };
    id = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(id); lenis.destroy(); lenisRef.current = null; };
  }, []);
}

function ScrollTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (lenisRef.current) lenisRef.current.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function Shell() {
  const [showLoader, setShowLoader] = useState(() => !sessionStorage.getItem("be_intro"));
  const [ready, setReady] = useState(() => Boolean(sessionStorage.getItem("be_intro")));
  useLenis();
  const done = useCallback(() => { sessionStorage.setItem("be_intro", "1"); setShowLoader(false); }, []);
  useEffect(() => {
    if (!showLoader) return undefined;
    const t = setTimeout(() => setReady(true), 1650);
    return () => clearTimeout(t);
  }, [showLoader]);

  return (
    <IntroCtx.Provider value={ready}>
      <div className="grain min-h-screen">
        <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[70] bg-[#d4af37] text-black px-4 py-2 rounded" data-testid="skip-link">Skip to content</a>
        <ScrollTop />
        <Nav />
        <main id="main">
          <Suspense fallback={<div className="min-h-screen" />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/platforms" element={<Platforms />} />
              <Route path="/indril" element={<Indril />} />
              <Route path="/urgent-sell-buy" element={<Market />} />
              <Route path="/boss-ai-os" element={<AIOS />} />
              <Route path="/founder" element={<Navigate to="/founder/zahir-patel" replace />} />
              <Route path="/founder/zahir-patel" element={<Founder />} />
              <Route path="/news" element={<News />} />
              <Route path="/news/:slug" element={<Article />} />
              <Route path="/vision" element={<Vision />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/legal" element={<Legal />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </main>
        <Footer />
        {showLoader ? <Loader onDone={done} /> : null}
        <Toaster theme="dark" position="bottom-right" richColors />
      </div>
    </IntroCtx.Provider>
  );
}

export default function App() {
  return (
    <MotionConfig reducedMotion="user">
      <BrowserRouter>
        <ContentProvider>
          <Shell />
        </ContentProvider>
      </BrowserRouter>
    </MotionConfig>
  );
}
